import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ImagePlus } from "lucide-react";
import { IMG } from "./siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_KEY = "nayara_site_token";
const siteHeaders = () => { const t = localStorage.getItem(SITE_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const SCRIPT = { fontFamily: "'Pinyon Script', cursive" };
const ease = [0.2, 0.7, 0.2, 1];

const STEPS = [
  { n: "01", t: "Printed", d: "True-to-artwork digital colour" },
  { n: "02", t: "Fused", d: "Heat-set for lasting vibrancy" },
  { n: "03", t: "Cut", d: "Precision cutting in-house" },
  { n: "04", t: "Stitched", d: "Couture finishing, any scale" },
];

const CSS = `
@keyframes nyStep{0%,18%{background:#2B1A10;color:#FBF7F1;border-color:#2B1A10}25%,100%{background:transparent;color:#3A2416;border-color:rgba(123,69,32,.25)}}
@keyframes nyNeedle{0%{top:0%}100%{top:100%}}
@keyframes nyDrift{0%,100%{transform:translateX(0)}50%{transform:translateX(-24px)}}
.ny-step{animation:nyStep 8s ease-in-out infinite}
.ny-needle{animation:nyNeedle 4.5s cubic-bezier(.6,0,.4,1) infinite alternate}
.ny-wm{animation:nyDrift 16s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.ny-step,.ny-needle,.ny-wm{animation:none}}
`;

export function useAdminImages() {
  const [cfg, setCfg] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const load = () => axios.get(`${API}/site/settings`).then((r) => setCfg(r.data || {})).catch(() => {});
  useEffect(() => {
    load();
    if (localStorage.getItem(SITE_KEY) && new URLSearchParams(window.location.search).has("admin"))
      axios.get(`${API}/site/admin/me`, { headers: siteHeaders() }).then(() => setIsAdmin(true)).catch(() => {});
  }, []);
  const src = (slot, fb) => (cfg[slot] ? `${API}/site/fabric-images/${cfg[slot]}` : fb);
  return { isAdmin, src, reload: load };
}

export function ChangeBtn({ slot, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("slot", slot); fd.append("image", f);
    try { await axios.post(`${API}/site/settings/image`, fd, { headers: siteHeaders() }); onDone(); }
    catch (err) { alert(err.response?.data?.detail || "Upload aagala"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} aria-label="Change photo"
        className="absolute left-1/2 top-4 z-20 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#2B1A10]/85 px-4 py-2.5 text-[10.5px] uppercase tracking-[0.2em] text-[#F4E7D3] shadow-lg backdrop-blur hover:bg-[#2B1A10]">
        <ImagePlus className="h-4 w-4" />{busy ? "Uploading..." : "Change"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1, delay, ease }}>{children}</motion.div>
  );
}

export default function AboutPremium() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const yB = useTransform(scrollYProgress, [0, 1], [-30, 90]);
  const { isAdmin, src, reload } = useAdminImages();
  const words = "A digital textile manufacturer for fashion, events &".split(" ");

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#FBF7F1] py-16 text-[#3A2416] lg:py-24" data-testid="about-premium">
      <style>{CSS}</style>
      <div aria-hidden="true" className="ny-wm pointer-events-none absolute -right-10 top-6 select-none text-[220px] leading-none text-[#7B4520]/[0.05] lg:text-[300px]" style={SCRIPT}>Nayara</div>

      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-20 lg:px-12">
        <div>
          <Reveal className="flex items-center gap-4 text-[10.5px] uppercase tracking-[0.4em] text-[#7B4520]">
            <motion.span className="h-px w-12 origin-left bg-[#7B4520]" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease }} />
            About Nayara Studio
          </Reveal>

          <h2 className="mt-7 text-[clamp(38px,4.2vw,62px)] font-light leading-[1.08] text-[#2B1A10]" style={SERIF}>
            {words.map((w, i) => (
              <motion.span key={i} className="mr-[0.25em] inline-block" initial={{ opacity: 0, y: "0.6em", rotate: 2 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 + i * 0.07, ease }}>{w}</motion.span>
            ))}
            <motion.span className="inline-block text-[1.25em] text-[#7B4520]" style={SCRIPT} initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.7, ease }}>décor.</motion.span>
          </h2>

          <Reveal delay={0.3}>
            <p className="mt-8 max-w-[560px] text-[16px] font-light leading-[1.9] text-[#5E4634]">
              Nayara Studio is a premium digital textile printing and fabric manufacturing house. We produce fashion fabrics, kurtis, dress materials and event drapes alongside photography, wedding and stage backdrops — printed, fused, cut and stitched entirely in-house.
            </p>
            <p className="mt-4 max-w-[560px] text-[16px] font-light leading-[1.9] text-[#5E4634]">
              From boutiques and fashion labels to event companies and photographers, brands trust us for true-to-artwork colour, couture finishing and dependable delivery at any scale.
            </p>
          </Reveal>

          <Reveal delay={0.45} className="mt-10 grid max-w-[560px] grid-cols-2 gap-3 sm:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.n} className="ny-step rounded-sm border px-3 py-4" style={{ animationDelay: `${i * 2}s` }} title={s.d}>
                <div className="text-[10px] tracking-[0.3em] opacity-70">{s.n}</div>
                <div className="mt-1 text-xl italic" style={SERIF}>{s.t}</div>
              </div>
            ))}
          </Reveal>

          <Reveal delay={0.6} className="mt-10">
            <Link to="/about" className="group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.24em] text-[#7B4520]" data-testid="about-link">
              <span className="relative">Our Story<span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-100 bg-[#7B4520] transition-transform duration-500 group-hover:scale-x-50" /></span>
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#7B4520]/40 transition-all duration-500 group-hover:rotate-45 group-hover:bg-[#7B4520] group-hover:text-[#FBF7F1]"><ArrowUpRight className="h-4 w-4" /></span>
            </Link>
          </Reveal>
        </div>

        <div className="relative h-[560px] lg:h-[640px]">
          <div className="absolute bottom-6 left-[46%] top-6 w-px bg-[#7B4520]/20">
            <span className="ny-needle absolute -left-[3px] h-[7px] w-[7px] rounded-full bg-[#7B4520]" />
          </div>

          <motion.div style={{ y: yA }} className="absolute left-0 top-0 h-[78%] w-[52%]">
            <div className="absolute -inset-3 translate-x-3 translate-y-3 border border-[#C9A27A]" />
            <motion.div className="relative h-full w-full overflow-hidden" initial={{ clipPath: "inset(100% 0 0 0)" }}
              whileInView={{ clipPath: "inset(0% 0 0 0)" }} viewport={{ once: true }} transition={{ duration: 1.4, ease }}>
              <motion.img src={src("about1", IMG.printMachine)} alt="Digital printing at Nayara Studio" className="h-full w-full object-cover"
                initial={{ scale: 1.25 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 2.2, ease }} />
              <span className="absolute bottom-4 left-4 bg-[#FBF7F1]/90 px-3 py-1.5 text-[10px] uppercase tracking-[0.26em] text-[#7B4520]">The press</span>
            </motion.div>
            {isAdmin && <ChangeBtn slot="about1" onDone={reload} />}
          </motion.div>

          <motion.div style={{ y: yB }} className="absolute bottom-0 right-0 h-[72%] w-[50%]">
            <motion.div className="relative h-full w-full overflow-hidden rounded-t-[200px]" initial={{ clipPath: "inset(0 0 100% 0)" }}
              whileInView={{ clipPath: "inset(0 0 0% 0)" }} viewport={{ once: true }} transition={{ duration: 1.4, delay: 0.25, ease }}>
              <motion.img src={src("about2", IMG.printedRack)} alt="Printed fabrics by Nayara Studio" className="h-full w-full object-cover"
                initial={{ scale: 1.25 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 2.2, delay: 0.25, ease }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B1A10]/55 via-transparent to-transparent" />
              <span className="absolute bottom-5 right-5 text-3xl leading-none text-[#FBF7F1]" style={SCRIPT}>Our prints</span>
            </motion.div>
            {isAdmin && <ChangeBtn slot="about2" onDone={reload} />}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
